import React, { useState } from 'react';
import { Box, Grid, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useClinicalSession } from '../../contexts/ClinicalSessionContext';
import { ACM } from '../../constants/UserRoles';
import useAllStudentGoalsData from '../../hooks/useAllStudentGoalsData';
import useAllStudentBehaviorData from '../../hooks/useAllStudentsBehaviorData';
import ClinicianStudentCard from './components/ClinicianStudentCard';
import TimeOfSessionCard from './components/TimeOfSessionCard';
import SessionDetailsCard from './components/SessionDetailsCard';
import SessionSummaryCard from './components/SessionSummaryCard';
import BillingInformationCard from './components/BillingInformationCard';
import GoalsDataCard from './components/GoalsDataCard';
import QuickTracksDataCard from './components/QuickTracksDataCard';
import ObservationsCard from './components/Observations';
import AbsentNotesCard from './components/AbsentNotesCard';
import TeamSharingSection from './components/TeamSharingSection';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import { useClinicalSessionState } from './hooks/useClinicalSessionState';
import { useClinicalSessionSaveHandlers } from './hooks/useClinicalSessionSaveHandlers';
import { SESSION_LENGTH } from './constants';

const ClinicalSession = ({ sessionId, user, userName }) => {
  const {
    getSession,
    updateSessionData,
    clearSessionData,
    closeSession,
    setGoalEntryDraft,
    removeGoalEntryDraft,
    setQuickTrackDraft,
    removeQuickTrackDraft,
    setIsSaving,
    maximizeSession,
  } = useClinicalSession();

  const [clearSessionDialogOpen, setClearSessionDialogOpen] = useState(false);

  const session = getSession(sessionId);
  const sessionData = session?.sessionData || {};

  // Fetch student goals and behavior data for activity log change tracking
  const sessionState = useClinicalSessionState({
    sessionId,
    session,
    sessionData,
    user,
    userName,
    updateSessionData,
  });

  const {
    currentUser,
    typeOfUser,
    preRegisteredUsers,
    teachersForStudent,
    isSavingSessionNotes,
    setIsSavingSessionNotes,
    goalsDataEntryRef,
    quickTracksDataEntryRef,
    goalsDataCardRef,
    quickTracksDataCardRef,
    normalizedUserSubRole,
    isEditingExistingSession,
    isCurrentUserClinicalAuthor,
    shouldLockSessionType,
    isPreRegistered,
    isDraftSession,
  } = sessionState;

  const { data: studentGoalsData = [] } = useAllStudentGoalsData(
    currentUser?.uid,
    typeOfUser
  );
  const { data: studentBehaviorData = [] } = useAllStudentBehaviorData(
    currentUser?.uid,
    typeOfUser
  );

  const { handleSaveDraft, handleSaveSession } = useClinicalSessionSaveHandlers(
    {
      sessionId,
      sessionData,
      session,
      user,
      typeOfUser,
      preRegisteredUsers,
      currentUser,
      goalsDataEntryRef,
      quickTracksDataEntryRef,
      goalsDataCardRef,
      quickTracksDataCardRef,
      updateSessionData,
      isSavingSessionNotes,
      setIsSavingSessionNotes,
      setIsSaving,
      clearSessionData,
      closeSession,
      maximizeSession,
      studentGoalsData,
      studentBehaviorData,
    }
  );

  const handleFieldChange = (field, value) => {

    const parseMinutesFromLabel = (label) => {
      if (!label || label === 'Other') return null;
      const hourMatch = label.match(/(\d+)\s*Hr[s]?/i);
      const minMatch = label.match(/(\d+)\s*Min[s]?/i);
      let minutes = 0;
      if (hourMatch) {
        minutes += parseInt(hourMatch[1], 10) * 60;
      }
      if (minMatch) {
        minutes += parseInt(minMatch[1], 10);
      }

      if (!hourMatch && !minMatch) {
        const numberMatch = label.match(/(\d+)/);
        if (numberMatch) {
          minutes = parseInt(numberMatch[1], 10);
        }
      }
      return Number.isFinite(minutes) ? minutes : null;
    };


    const getSessionLengthForMinutes = (mins) => {
      if (mins == null) return { sessionLength: '', customSessionLength: '' };

      const options = SESSION_LENGTH.filter((l) => l !== 'Other')
        .map((l) => ({
          label: l,
          minutes: parseMinutesFromLabel(l),
        }))
        .filter((opt) => opt.minutes != null)
        .sort((a, b) => a.minutes - b.minutes);


      if (mins > 240) {

        let units = 0;
        if (mins >= 8) {
          units = Math.floor((mins - 8) / 15) + 1;
        }
        return {
          sessionLength: 'Other',
          customSessionLength: String(units),
        };
      }


      let chosen = options[0];
      for (const opt of options) {
        if (opt.minutes <= mins) {
          chosen = opt;
        } else {
          break;
        }
      }
      return { sessionLength: chosen.label, customSessionLength: '' };
    };


    const toDayjs = (val) => {
      if (!val) return null;

      if (dayjs.isDayjs(val)) {
        return val.isValid() ? val : null;
      }
      const parsed = dayjs(val);
      return parsed.isValid() ? parsed : null;
    };


    const alignTimeWithBaseDay = (base, timeVal) => {
      if (!base || !timeVal) return null;
      return base
        .set('hour', timeVal.hour())
        .set('minute', timeVal.minute())
        .set('second', timeVal.second())
        .set('millisecond', timeVal.millisecond());
    };


    if (field === 'endTime') {
      const start = toDayjs(sessionData.time);
      const rawEnd = toDayjs(value);
      const end = alignTimeWithBaseDay(start, rawEnd);
      if (start && end && end.isAfter(start)) {
        const diffMins = end.diff(start, 'minute');
        const { sessionLength, customSessionLength } =
          getSessionLengthForMinutes(diffMins);
        updateSessionData(sessionId, {
          endTime: end,
          sessionLength,
          customSessionLength,
        });
      } else {

        return;
      }
    } else if (field === 'sessionLength') {
      const start = toDayjs(sessionData.time);
      if (start) {
        const mins = parseMinutesFromLabel(value);
        if (mins != null) {
          const newEnd = start.add(mins, 'minute');
          updateSessionData(sessionId, {
            sessionLength: value,
            endTime: newEnd,
            customSessionLength: value === 'Other' ? sessionData.customSessionLength : '',
          });
          return;
        }
      }

      updateSessionData(sessionId, { [field]: value });
      if (value !== 'Other') {
        updateSessionData(sessionId, { customSessionLength: '' });
      }
    } else if (field === 'customSessionLength') {

      const start = toDayjs(sessionData.time);
      if (start && sessionData.sessionLength === 'Other' && value) {
        const units = typeof value === 'string' ? parseInt(value, 10) : value;
        if (!isNaN(units) && units > 0) {
          const minutes = units * 15;
          const newEnd = start.add(minutes, 'minute');
          updateSessionData(sessionId, {
            customSessionLength: String(units),
            endTime: newEnd,
          });
          return;
        }
      }
      updateSessionData(sessionId, { [field]: value });
    } else if (field === 'time') {

      const start = toDayjs(value);
      if (start) {
        let mins = null;
        if (sessionData.sessionLength === 'Other' && sessionData.customSessionLength) {

          const units = typeof sessionData.customSessionLength === 'string'
            ? parseInt(sessionData.customSessionLength, 10)
            : sessionData.customSessionLength;
          if (!isNaN(units) && units > 0) {
            mins = units * 15;
          }
        } else {
          mins = parseMinutesFromLabel(sessionData.sessionLength);
        }

        if (mins != null) {
          const newEnd = start.add(mins, 'minute');
          updateSessionData(sessionId, {
            time: start,
            endTime: newEnd,
          });
          return;
        }
      }
      updateSessionData(sessionId, { [field]: value });
    } else {
      updateSessionData(sessionId, { [field]: value });
    }
  };

  const handleAccessChange = (accessType, value) => {
    handleFieldChange(accessType, value);
  };

  const handleClearSession = () => {
    setClearSessionDialogOpen(true);
  };

  const handleConfirmClearSession = () => {
    clearSessionData(sessionId);
    closeSession(sessionId);
    setClearSessionDialogOpen(false);
  };

  const handleCancelClearSession = () => {
    setClearSessionDialogOpen(false);
  };

  const studentId = sessionData.studentId || user?.studentId || user?.id;
  const includeParentGuardian = studentId && !isPreRegistered(studentId);
  const shouldShowSharing =
    ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher;
  const canDisplayTeamSharing =
    shouldShowSharing &&
    (!isEditingExistingSession || isCurrentUserClinicalAuthor);

  if (!session) {
    return null;
  }

  const canDisplayDraftSaveButton =
    !isEditingExistingSession || isDraftSession;

  const submitButtonLabel =
    isEditingExistingSession && !isDraftSession
      ? 'Update Session'
      : 'Submit Session';
  const lastSavedLabel = sessionData?.lastSavedAt
    ? dayjs(sessionData.lastSavedAt).format('MMM D, YYYY h:mm A')
    : null;

  return (
    <Box sx={{ fontFamily: 'Lato, sans-serif' }}>
      {lastSavedLabel && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: 'rgba(0,0,0,0.6)', fontStyle: 'italic' }}
          >
            Last saved {lastSavedLabel}
          </Typography>
        </Box>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ClinicianStudentCard
            clinicianName={sessionData.clinicianName}
            clinicianNpi={sessionData.clinicianNpi}
            studentName={sessionData.studentName}
            onFieldChange={handleFieldChange}
            disableNpiInput={Boolean(currentUser?.provider?.npi)}
          />
        </Grid>

        <Grid item xs={12}>
          <TimeOfSessionCard
            date={sessionData.date}
            time={sessionData.time}
            endTime={sessionData.endTime}
            sessionLength={sessionData.sessionLength}
            customSessionLength={sessionData.customSessionLength}
            sessionAttended={sessionData.sessionAttended}
            onFieldChange={handleFieldChange}
          />
        </Grid>

        <Grid item xs={12}>
          <SessionDetailsCard
            setting={sessionData.setting}
            customSetting={sessionData.customSetting}
            groupSize={sessionData.groupSize}
            customGroupSize={sessionData.customGroupSize}
            groupType={sessionData.groupType}
            customGroupType={sessionData.customGroupType}
            absentReason={sessionData.absentReason}
            customAbsentReason={sessionData.customAbsentReason}
            sessionType={sessionData.sessionType}
            customSessionType={sessionData.customSessionType}
            onFieldChange={handleFieldChange}
            sessionAttended={sessionData.sessionAttended}


          />
        </Grid>

        {sessionData.sessionAttended === 'No' && (
          <Grid item xs={12}>
            <AbsentNotesCard
              absentNotes={sessionData.absentNotes}
              onFieldChange={handleFieldChange}
            />
          </Grid>
        )}

        {sessionData.sessionAttended !== 'No' && (
          <>
            <Grid item xs={12}>
              <SessionSummaryCard
                sessionSummary={sessionData.sessionSummary}
                onFieldChange={handleFieldChange}
              />
            </Grid>
            <Grid item xs={12}>
              <ObservationsCard
                observations={sessionData.observations}
                onFieldChange={handleFieldChange}
              />
            </Grid>

            <Grid item xs={12}>
              <BillingInformationCard
                isBillable={sessionData.isBillable}
                cptCodes={sessionData.cptCodes}
                sdpServiceCodes={sessionData.sdpServiceCodes}
                iepServiceTherapies={sessionData.iepServiceTherapies}
                nonBillableReason={sessionData.nonBillableReason}
                onFieldChange={handleFieldChange}
                subrole={normalizedUserSubRole}
                studentId={studentId}
                typeOfUser={typeOfUser}
              />
            </Grid>

            <Grid item xs={12}>
              <GoalsDataCard
                ref={goalsDataCardRef}
                sessionId={sessionId}
                user={user}
                userName={userName}
                onFieldChange={handleFieldChange}
                dataEntryRef={goalsDataEntryRef}
                draftEntries={sessionData.goalEntryDrafts || {}}
                onUpsertDraft={(goalId, draft) =>
                  setGoalEntryDraft(sessionId, goalId, draft)
                }
                onRemoveDraft={(goalId) => removeGoalEntryDraft(sessionId, goalId)}
                formState={sessionData}
                originalData={session?.originalData}
              />
            </Grid>

            <Grid item xs={12}>
              <QuickTracksDataCard
                ref={quickTracksDataCardRef}
                sessionId={sessionId}
                user={user}
                userName={userName}
                onFieldChange={handleFieldChange}
                dataEntryRef={quickTracksDataEntryRef}
                draftEntries={sessionData.quickTrackDrafts || {}}
                onUpsertDraft={(trackId, draft) =>
                  setQuickTrackDraft(sessionId, trackId, draft)
                }
                onRemoveDraft={(trackId) =>
                  removeQuickTrackDraft(sessionId, trackId)
                }
                formState={sessionData}
                originalData={session?.originalData}
              />
            </Grid>
          </>
        )}

        {canDisplayTeamSharing && (
          <Grid item xs={12}>
            <TeamSharingSection
              viewOnlyAccess={sessionData.viewOnlyAccess || []}
              teachersForStudent={teachersForStudent}
              includeParentGuardian={includeParentGuardian}
              onAccessChange={handleAccessChange}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}
          >
            <Button
              variant='outlined'
              onClick={handleClearSession}
              sx={{
                textTransform: 'none',
                px: 3,
                py: 1,
                color: '#9291ef',
                border: '1px solid #9291ef',
              }}
            >
              Clear Changes
            </Button>
            {canDisplayDraftSaveButton && (
              <Button
                variant='contained'
                onClick={handleSaveDraft}
                disabled={isSavingSessionNotes}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  bgcolor: '#9291ef',
                  '&:hover': {
                    bgcolor: '#7a79d9',
                  },
                }}
              >
                Save Changes
              </Button>
            )}
            <Button
              variant='contained'
              onClick={handleSaveSession}
              disabled={isSavingSessionNotes}
              sx={{
                textTransform: 'none',
                px: 3,
                py: 1,
                bgcolor: '#5c5abf',
                '&:hover': {
                  bgcolor: '#4a49a3',
                },
              }}
            >
              {submitButtonLabel}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <DeleteConfirmationDialog
        open={clearSessionDialogOpen}
        onClose={handleCancelClearSession}
        onConfirm={handleConfirmClearSession}
        title='Clear Session'
        message='Are you sure you want to clear this session? This action cannot be undone, and the session will close without saving any progress since the last saved time.'
        buttonLabel='Clear Session'
      />
    </Box>
  );
};

export default ClinicalSession;